import { useGetFolderPermissionsQuery } from '../gql/generated/GetFolderPermissions';
import type { FolderPermission } from './useFolderPermissions';

export function useFolderPermissionSet<
  T extends FolderPermission,
  K extends T,
>({
  checks,
  folderId,
}: {
  checks: Array<
    | K
    | {
        permission: K;
        isInitiallyPermitted?: boolean;
      }
  >;
  folderId: string | undefined;
}): Record<K, boolean> {
  const { data } = useGetFolderPermissionsQuery({
    skip: !folderId,
    variables: {
      folderId: folderId ?? '',
    },
  });

  const asset = data?.account?.asset;

  if (!asset || asset.__typename !== 'FolderAsset' || !asset.permissions) {
    return reduceChecks(
      checks.map((check) => {
        const permission = typeof check === 'string' ? check : check.permission;
        const isInitiallyPermitted =
          typeof check === 'string' ? false : check.isInitiallyPermitted;

        return {
          permission,
          value: isInitiallyPermitted ?? false,
        };
      })
    );
  }

  return reduceChecks(
    checks.map((check) => {
      const permission = typeof check === 'string' ? check : check.permission;

      return {
        permission,
        value: asset.permissions?.[permission] === true,
      };
    })
  );
}

const reduceChecks = (
  checks: { permission: FolderPermission; value: boolean }[]
) =>
  checks.reduce(
    (acc, curr) => {
      const perms = {
        ...acc,
      };

      perms[curr.permission] = curr.value;

      return perms;
    },
    {} as Record<FolderPermission, boolean>
  );
